import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { ExploreStateModel } from './explore.state.model';
import {
    Load,
    LoadSuccess,
    SortSearchResults,
    ResetSearchResults,
    CheckSearchResult,
    SetCheckedSearchResults,
    SelectCategory,
    Search,
    SetSearching,
    ResetExplore
} from './explore.actions';
import { ExploreService } from '../explore.service';
import { BookmarksService, BookmarkCategory } from 'src/app/bookmarks/bookmarks.service';
import { finalize } from 'rxjs/operators';
import { bookmarkSortTitleAsc } from 'src/app/bookmarks/components/bookmarks-sort/bookmarks-sort.component';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { exploreMenu } from '../data/explore-menu';

const { categories } = exploreMenu;

const initialState = {
  query: '',
  loading: true,
  searching: false,
  searchResults: [],
  sort: bookmarkSortTitleAsc,
  checkedSearchResults: [],
  selectedCategory: null
};

@State<ExploreStateModel>({
  name: 'explore',
  defaults: initialState
})
export class ExploreState {
  @Selector()
  static getSearchResults(state: ExploreStateModel) {
    if (state.searching) { // searching: natural order
      return state.searchResults;
    } else { // explicit sort
      const { ascending, field, alias } = state.sort;

      return state.searchResults.sort((a, b) => {
        const aValue = a[field]  || a[alias] || '';
        const bValue = b[field] || b[alias] || '';
        return ((ascending) ? 1 : -1) * aValue.localeCompare(bValue, [], {sensitivity: 'base'});
      });    
    }
  }

  @Selector()
  static getCheckedSearchResults(state: ExploreStateModel) {
    return state.checkedSearchResults;
  }

  @Selector()
  static getLoading(state: ExploreStateModel) {
    return state.loading;
  }

  @Selector()
  static getSort(state: ExploreStateModel) {
    return state.sort;
  }

  @Selector()
  static getSelectedCategory(state: ExploreStateModel) {
    return state.selectedCategory;
  }

  @Selector()
  static getSearchQuery(state: ExploreStateModel) {
    return state.query;
  }
  
  @Action(Load)
  load(context: StateContext<ExploreStateModel>, { category }: Load) {
    // Loading
    context.patchState({ loading: true });

    const lang = this.store.selectSnapshot(AuthState.getUserLanguage);

    // Special case for community bookmarks
    const results$ = category === BookmarkCategory.Public
      ? this.bookmarksService.getPublicBookmarks()
      : this.exploreService.getAnatomyCategory('', category, lang);

    results$
      .pipe(
        finalize(() => {
          context.patchState({ loading: false });
        })
      )
      .subscribe((searchResults) => {
        // id normalization
        searchResults.forEach((result) => {
          if (result.type === 'module') {
            result.id = result.module_name.replace('.json', '');
          }
        });
        context.dispatch(new LoadSuccess(searchResults));
      });
  }

  @Action(Search)
  search(context: StateContext<ExploreStateModel>, { query }: Search) {
    context.patchState({
      query: query,
      searching: true,
      loading: true
    });
    const lang = this.store.selectSnapshot(AuthState.getUserLanguage);

    // search category modules
    this.exploreService.search(query, [], lang)
      .pipe(
        finalize(() => context.patchState({ loading: false }))
      )
      .subscribe(
        (bookmarks) => context.dispatch(new LoadSuccess(bookmarks))
      );
  }

  @Action(SetSearching)
  setSearching(context: StateContext<ExploreStateModel>, { searching }: SetSearching) {
    context.patchState({
      searching: searching
    });
  }

  @Action(LoadSuccess)
  loadSuccess({ patchState }: StateContext<ExploreStateModel>, { searchResults }: LoadSuccess) {
    patchState({
      searchResults: searchResults
    });
  }

  @Action(SortSearchResults)
  sortSearchResults(context: StateContext<ExploreStateModel>, action: SortSearchResults) {
    const searchResults = context.getState().searchResults;
    
    context.patchState({
      sort: action.sort,
      searchResults: [...searchResults]
    });
  }

  @Action(ResetSearchResults)
  resetSearchResults(context: StateContext<ExploreStateModel>, action: ResetSearchResults) {
    context.patchState({
      searchResults: [],
      checkedSearchResults: []
    });
  }

  @Action(CheckSearchResult)
  checkSearchResult(context: StateContext<ExploreStateModel>, action: CheckSearchResult) {
    let checkedSearchResults = context.getState().checkedSearchResults;
    const exists = checkedSearchResults.some(bookmark => bookmark === action.id);

    if (action.checked && !exists)  {
      checkedSearchResults = [...checkedSearchResults, action.id];
    } else if (!action.checked) {
      checkedSearchResults = checkedSearchResults.filter(bookmark => bookmark !== action.id);
    }

    context.patchState({
      checkedSearchResults: checkedSearchResults
    });
  }

  @Action(SetCheckedSearchResults)
  setCheckedSearchResults(context: StateContext<ExploreStateModel>, action: SetCheckedSearchResults) {
    context.patchState({
      checkedSearchResults: action.ids
    });
  }
  
  @Action(SelectCategory)
  selectCategory(context: StateContext<ExploreStateModel>, { tag }: SelectCategory) {
    context.patchState({
      selectedCategory: categories.find((category) => category.tag === tag)
    });
  }

  @Action(ResetExplore)
  resetExplore(context: StateContext<ExploreStateModel>, action: ResetExplore) {
    context.setState(initialState);
  }

  constructor(
    private exploreService: ExploreService,
    private bookmarksService: BookmarksService,
    private store: Store
  ) { }
}
